import React, { useState } from 'react';
import { resolveCommentTarget } from '../../../stores/projectCommentsRemote';
import { landOnComment } from '../../../stores/projectCommentLanding';
import { ArrowLeft, Bell, CheckCheck, Gift, LifeBuoy, Megaphone, Sparkles } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { useSocialStore } from '../../../stores/socialStore';
import { usePendingGiftCount, useUnreadNotificationCount, useVisibleNotifications } from '../../../hooks/useVisibleNotifications';
import { useCurrentView } from '../../../hooks/useCurrentView';
import { useSettingsDeepLink } from '../../../stores/settingsDeepLink';
import { useWhatsNewStore } from '../../../stores/whatsNewStore';
import { useNotificationPrefsStore } from '../../../stores/notificationPrefsStore';
import { LinkifiedText } from './LinkifiedText';
import { useGiftStore } from '../../../stores/giftStore';
import { CoinAmount } from '../../CoinAmount';
import { formatCoinFull } from '../../../utils/coin';
import { GIFT_STATUS_LABEL, giftStatus, timeLeftLabel } from '../../../utils/coinGift';
import {
  EMPTY_TAB_COPY,
  NOTIFICATION_TABS,
  inTab,
  noticeAction,
  type NoticeAction,
  type NotificationTab,
} from '../../../utils/notificationFeed';
import type { AppNotification, CoinGift, ReleaseNotice, SocialNotification } from '../../../types';

const VERBS: Record<SocialNotification['type'], string> = {
  follow: 'started following you',
  like: 'liked your post',
  comment: 'commented on your post',
  reply: 'replied to your comment',
  copy: 'copied your template',
  mention: 'mentioned you',
  message: 'sent you a message',
  repost: 'reposted your post',
  network_request: 'wants to add you to their network',
  network_accepted: 'accepted your network request',
  pact_request: 'invited you to a pact',
  pact_accepted: 'accepted your pact',
  pact_nudge: 'nudged you about your pact',
  request_claimed: 'offered to help with your request',
  request_fulfilled: 'confirmed you fulfilled their request',
  project_invite: 'invited you to collaborate on a project',
  task_assigned: 'assigned you a task',
  comment_mention: 'mentioned you in a comment',
};

/** One-shot flag: the Projects page opens its invitations inbox. */
export const OPEN_PROJECT_INVITES_KEY = 'metanoia-open-project-invites';

/** One-shot flag: the Social domain page opens straight to the Pacts tab. */
export const OPEN_PACTS_KEY = 'metanoia-open-social-pacts';

/** A release has a day, not a time — "4 Oct 2026", read as a local calendar day. */
function formatReleaseDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return format(new Date(y, m - 1, d), 'd MMM yyyy');
}

/**
 * The notification center body — shared by the global header avatar and
 * Connect's ribbon bell. Click-through navigates to the right PAGE too
 * (the host may be anywhere in the app, not just on Connect).
 *
 * ⭐ PER-ITEM READ (owner, 2026-10-04): opening the center reads nothing — an
 * item is read when it is opened, or by "Mark all read". Unread rows keep their
 * highlight until then.
 * Social items navigate straight to their page (they are one line, nothing more
 * to read); notices from the app open a detail view here first.
 */
export function NotificationPanel({
  onClose,
  embedded,
}: {
  onClose: () => void;
  /**
   * Render in the FLOW rather than as its own dropdown.
   *
   * ⚠️ The panel positions and decorates itself as a floating card. Inside the
   * avatar menu it is a section of a card that already exists, so absolute
   * positioning would tear it out of the menu and its border/shadow would draw
   * a second card on top of the first.
   */
  embedded?: boolean;
}) {
  const {
    openProfile, openDmThread, openPostDetail,
    markNotificationRead, markAllNotificationsAsRead, loadMoreNotifications,
  } = useSocialStore();
  const hasMore = useSocialStore((s) => s.hasMoreNotifications);
  const loadingMore = useSocialStore((s) => s.loadingMoreNotifications);
  const notifications = useVisibleNotifications();
  const unreadCount = useUnreadNotificationCount();
  // gifts need an ANSWER, not a read — "Mark all read" can't clear them, so it
  // only shows when something it can clear is unread
  const pendingGifts = usePendingGiftCount();
  const [tab, setTab] = useState<NotificationTab>('all');
  const updatesMuted = useNotificationPrefsStore((s) => !s.inApp.updates);
  const [detail, setDetail] = useState<
    | { source: 'app'; item: AppNotification }
    | { source: 'release'; item: ReleaseNotice }
    // by id: the gift changes under the view when it's accepted or declined
    | { source: 'gift'; id: string }
    | null
  >(null);

  const shown = notifications.filter((entry) => inTab(entry, tab));

  const handleClick = async (notification: SocialNotification) => {
    void markNotificationRead('social', notification.id);
    onClose();

    if (notification.type === 'pact_request' || notification.type === 'pact_accepted' || notification.type === 'pact_nudge') {
      // pacts hub = Social domain page Pacts tab (one-shot tab flag)
      try {
        localStorage.setItem(OPEN_PACTS_KEY, '1');
        localStorage.setItem('selectedDomainId', 'social');
      } catch { /* storage unavailable — land on the domain page default tab */ }
      useCurrentView.getState().setCurrentView('domain');
      return;
    }
    if (notification.type === 'request_claimed' || notification.type === 'request_fulfilled') {
      // requests hub = Social domain page Requests tab
      try {
        localStorage.setItem('socialDomainActiveTab', 'requests');
        localStorage.setItem('selectedDomainId', 'social');
      } catch { /* storage unavailable — land on the domain page default tab */ }
      useCurrentView.getState().setCurrentView('domain');
      return;
    }

    if (notification.type === 'comment_mention') {
      const target = notification.itemId ? await resolveCommentTarget(notification.itemId) : null;
      landOnComment(target);
      try { useCurrentView.getState().setCurrentView('projects'); } catch { /* Zustand updates memory before its persistence write; Projects still opens. */ }
      return;
    }

    if (notification.type === 'task_assigned') {
      try {
        if (notification.itemId) localStorage.setItem('selectedProjectId', notification.itemId);
        else localStorage.removeItem('selectedProjectId');
      } catch { /* storage unavailable — still land on Projects */ }
      useCurrentView.getState().setCurrentView('projects');
      return;
    }

    if (notification.type === 'project_invite') {
      // land on Projects with the invitations inbox open (one-shot flag).
      try { localStorage.setItem(OPEN_PROJECT_INVITES_KEY, '1'); } catch { /* storage unavailable */ }
      useCurrentView.getState().setCurrentView('projects');
      return;
    }

    if (notification.type === 'follow' || notification.type === 'network_request' || notification.type === 'network_accepted') {
      // the profile (now its own page) carries the accept/decline actions
      openProfile(notification.sender.id);
      return;
    }
    if (notification.type === 'message') {
      useCurrentView.getState().setCurrentView('discover');
      const { data } = await supabase
        .from('user_profiles')
        .select('user_level')
        .eq('id', notification.sender.id)
        .maybeSingle();
      openDmThread({
        id: notification.sender.id,
        name: notification.sender.name,
        avatar: notification.sender.avatar,
        level: data?.user_level || 1,
      });
      return;
    }
    if (notification.postId) {
      useCurrentView.getState().setCurrentView('discover');
      openPostDetail(notification.postId);
    } else {
      openProfile(notification.sender.id);
    }
  };

  const openApp = (notification: AppNotification) => {
    void markNotificationRead('app', notification.id);
    setDetail({ source: 'app', item: notification });
  };

  // reading a release reads everything older too (owner, 2026-10-04)
  const openRelease = (release: ReleaseNotice) => {
    void useWhatsNewStore.getState().markSeen(release.version);
    setDetail({ source: 'release', item: release });
  };

  const markAllRead = () => {
    void markAllNotificationsAsRead();
    void useWhatsNewStore.getState().markAllSeen();
  };

  const followAction = (action: NoticeAction) => {
    onClose();
    useSettingsDeepLink.getState().open(action.target, action.ticketId);
    useCurrentView.getState().setCurrentView('settings');
  };

  const frame = embedded
    ? 'flex max-h-[min(28rem,70vh)] flex-col overflow-hidden'
    : 'absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[min(30rem,75vh)] bg-card border border-border rounded-xl shadow-xl z-50 flex flex-col overflow-hidden';

  if (detail?.source === 'gift') {
    return <GiftDetail frame={frame} giftId={detail.id} onBack={() => setDetail(null)} />;
  }

  if (detail) {
    const action = detail.source === 'app' ? noticeAction(detail.item) : null;
    return (
      <div className={frame}>
        <div className="flex items-center gap-2 px-2 py-2 border-b border-border">
          <button
            type="button"
            onClick={() => setDetail(null)}
            aria-label="Back to notifications"
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
        </div>
        {detail.source === 'release' ? (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <p className="text-sm font-semibold text-foreground">What&apos;s new in {detail.item.version}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{formatReleaseDay(detail.item.date)}</p>
            <ul className="mt-3 space-y-2 list-disc pl-4 text-sm text-foreground">
              {detail.item.notes.map((note) => (
                <li key={note} className="break-words">{note}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <p className="text-sm font-semibold text-foreground">{detail.item.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDistanceToNow(new Date(detail.item.createdAt), { addSuffix: true })}
            </p>
            {detail.item.body && (
              <p className="mt-3 text-sm text-foreground whitespace-pre-wrap break-words">
                <LinkifiedText text={detail.item.body} />
              </p>
            )}
          </div>
        )}
        {action && (
          <div className="px-4 py-3 border-t border-border">
            <button
              type="button"
              onClick={() => followAction(action)}
              className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={frame}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
        {unreadCount - pendingGifts > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {/* ⚠️ Five tabs don't fit a narrow panel (four fit a 280px pane with ~5px
          to spare) — the row scrolls sideways instead of squeezing or wrapping.
          ⚠️⚠️ `shrink-0` IS LOAD-BEARING: a scroll container's min-height is 0 in a
          flex column, so with a long list the row was squeezed to 17px of its
          28px and the tabs were cut in half (found live, 2026-10-05). */}
      <div role="tablist" aria-label="Notification types" className="flex shrink-0 gap-1 px-3 py-2 border-b border-border overflow-x-auto [scrollbar-width:none]">
        {NOTIFICATION_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              tab === t.id ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {shown.length === 0 ? (
          <div className="text-center px-4 py-10">
            <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-xs text-muted-foreground">
              {/* muted is not "none yet" — say why the tab is empty, and where to change it */}
              {tab === 'updates' && updatesMuted
                ? 'App updates are turned off — turn them on in Settings → Notifications.'
                : EMPTY_TAB_COPY[tab]}
            </p>
          </div>
        ) : (
          shown.map((entry) => {
            // a gift is "unread" while it waits for an answer
            const unread = entry.source === 'gift' ? giftStatus(entry.item) === 'pending' : !entry.item.read;
            const rowClass = `w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border last:border-b-0 transition-colors hover:bg-muted/50 ${
              unread ? 'bg-primary/5' : ''
            }`;
            const time = (
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatDistanceToNow(new Date(entry.item.createdAt), { addSuffix: true })}
              </p>
            );
            const dot = unread && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" aria-label="Unread" />;

            if (entry.source === 'gift') {
              const gift = entry.item;
              const status = giftStatus(gift);
              return (
                <button key={`gift-${gift.id}`} onClick={() => setDetail({ source: 'gift', id: gift.id })} className={rowClass}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Gift className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-semibold">A gift of {formatCoinFull(gift.amountCp)}</p>
                    {gift.note && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{gift.note}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {status === 'pending' ? `Waiting for you · ${timeLeftLabel(gift.expiresAt)}` : GIFT_STATUS_LABEL[status]}
                    </p>
                  </div>
                  {dot}
                </button>
              );
            }

            if (entry.source === 'release') {
              const release = entry.item;
              return (
                <button key={release.id} onClick={() => openRelease(release)} className={rowClass}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-semibold">What&apos;s new in {release.version}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{release.notes[0]}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatReleaseDay(release.date)}</p>
                  </div>
                  {dot}
                </button>
              );
            }

            if (entry.source === 'app') {
              const notice = entry.item;
              return (
                <button key={`app-${notice.id}`} onClick={() => openApp(notice)} className={rowClass}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    {notice.kind === 'team_message' ? <Megaphone className="w-4 h-4" /> : <LifeBuoy className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-semibold line-clamp-2">{notice.title}</p>
                    {notice.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{notice.body}</p>}
                    {time}
                  </div>
                  {dot}
                </button>
              );
            }

            const notification = entry.item;
            return (
              <button key={`social-${notification.id}`} onClick={() => handleClick(notification)} className={rowClass}>
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold shrink-0 overflow-hidden">
                  {notification.sender.avatar ? (
                    <img src={notification.sender.avatar} alt={notification.sender.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    notification.sender.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">
                    <span className="font-semibold">{notification.sender.name}</span>{' '}
                    {notification.type === 'like' && notification.commentId
                      ? 'liked your reply'
                      : VERBS[notification.type] || 'sent a notification'}
                  </p>
                  {time}
                </div>
                {dot}
              </button>
            );
          })
        )}

        {hasMore && (
          <div className="px-4 py-3 text-center">
            <button
              type="button"
              onClick={() => void loadMoreNotifications()}
              disabled={loadingMore}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One gift, with its answer. ⚠️ Accept is the ONLY way a gift becomes Coin and it
 * happens on the server (accept_coin_gift); this view shows what the server said.
 * The "A gift" toast then fires from the new ledger row, as every credit does.
 */
function GiftDetail({ frame, giftId, onBack }: { frame: string; giftId: string; onBack: () => void }) {
  const gift = useGiftStore((s) => s.gifts.find((g) => g.id === giftId)) as CoinGift | undefined;
  const busy = useGiftStore((s) => !!s.busy[giftId]);
  const [error, setError] = useState('');
  // declining can't be undone — it takes a second, deliberate tap
  const [confirmDecline, setConfirmDecline] = useState(false);

  const answer = async (how: 'accept' | 'decline') => {
    setError('');
    const res = await useGiftStore.getState()[how](giftId);
    if (!res.ok) setError(res.error);
  };

  const status = gift ? giftStatus(gift) : null;

  return (
    <div className={frame}>
      <div className="flex items-center gap-2 px-2 py-2 border-b border-border">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to notifications"
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
      </div>
      {!gift || !status ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">This gift is no longer available.</p>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <p className="text-sm font-semibold text-foreground">A gift from the Metanoia team</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDistanceToNow(new Date(gift.createdAt), { addSuffix: true })}
            </p>
            <p className="mt-3 text-lg font-semibold text-foreground" aria-label={formatCoinFull(gift.amountCp)}>
              <CoinAmount cp={gift.amountCp} />
            </p>
            {gift.note && (
              <p className="mt-2 text-sm text-foreground whitespace-pre-wrap break-words">
                <LinkifiedText text={gift.note} />
              </p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              {status === 'pending'
                ? `Accept it to add it to your Coin — ${timeLeftLabel(gift.expiresAt)}.`
                : status === 'accepted'
                  ? 'Accepted — it’s in your Coin.'
                  : status === 'declined'
                    ? 'You declined this gift.'
                    : 'This gift expired before it was accepted.'}
            </p>
            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          </div>
          {status === 'pending' && confirmDecline && (
            <div className="px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Decline this gift? You can&apos;t accept it afterwards.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDecline(false)}
                  disabled={busy}
                  className="flex-1 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
                >
                  Keep it
                </button>
                <button
                  type="button"
                  onClick={() => void answer('decline')}
                  disabled={busy}
                  className="flex-1 rounded-lg border border-destructive/50 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                >
                  {busy ? 'Working…' : 'Yes, decline'}
                </button>
              </div>
            </div>
          )}
          {status === 'pending' && !confirmDecline && (
            <div className="flex gap-2 px-4 py-3 border-t border-border">
              <button
                type="button"
                onClick={() => setConfirmDecline(true)}
                disabled={busy}
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => void answer('accept')}
                disabled={busy}
                className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {busy ? 'Working…' : 'Accept'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
